// features/super-admin/pages/ErrorLogsPage.tsx
/**
 * SUPER ADMIN — SYSTEM ERROR LOGS
 * Enterprise-grade error monitoring with filters, bulk delete, and detail navigation.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Search, AlertTriangle, XCircle, AlertCircle,
    Info, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    X, Calendar, Trash2, Eye, RefreshCw, Server, Shield,
    Clock, Globe, Monitor, Database, Wifi, Zap, Download,
    Filter, CheckSquare, Square, Copy, ExternalLink,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface ErrorLog {
    id: string;
    tenantId: string | null;
    userId: string | null;
    errorCode: string | null;
    errorType: string;
    severity: string;
    message: string;
    requestMethod: string | null;
    requestPath: string | null;
    responseStatus: number | null;
    ipAddress: string | null;
    userAgent: string | null;
    durationMs: number | null;
    createdAt: string;
}

interface ErrorLogsResponse {
    logs: ErrorLog[];
    totalCount: number;
    summary: {
        totalErrors: number;
        criticalErrors: number;
        serverErrors: number;
        validationErrors: number;
        todayErrors: number;
    };
}

// ============================================================
// CONSTANTS
// ============================================================

const SEVERITY_CONFIG: Record<string, { variant: 'danger' | 'warning' | 'info' | 'neutral'; icon: React.ElementType; label: string; color: string; bg: string }> = {
    critical: { variant: 'danger', icon: XCircle, label: 'Critical', color: 'text-rose-600', bg: 'bg-rose-50' },
    error: { variant: 'warning', icon: AlertTriangle, label: 'Error', color: 'text-amber-600', bg: 'bg-amber-50' },
    warning: { variant: 'info', icon: AlertCircle, label: 'Warning', color: 'text-blue-600', bg: 'bg-blue-50' },
    info: { variant: 'neutral', icon: Info, label: 'Info', color: 'text-slate-600', bg: 'bg-slate-50' },
};

const ERROR_TYPE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string; bg: string }> = {
    validation: { label: 'Validation', icon: Shield, color: 'text-amber-600', bg: 'bg-amber-50' },
    unauthorized: { label: 'Unauthorized', icon: Shield, color: 'text-orange-600', bg: 'bg-orange-50' },
    forbidden: { label: 'Forbidden', icon: Shield, color: 'text-rose-600', bg: 'bg-rose-50' },
    not_found: { label: 'Not Found', icon: Search, color: 'text-slate-600', bg: 'bg-slate-50' },
    conflict: { label: 'Conflict', icon: AlertTriangle, color: 'text-purple-600', bg: 'bg-purple-50' },
    server_error: { label: 'Server Error', icon: Server, color: 'text-red-600', bg: 'bg-red-50' },
    database_error: { label: 'Database Error', icon: Database, color: 'text-pink-600', bg: 'bg-pink-50' },
    unknown: { label: 'Unknown', icon: Info, color: 'text-slate-600', bg: 'bg-slate-50' },
};

// ============================================================
// FORMATTERS
// ============================================================

const formatDateTime = (date: string) => {
    try { return new Date(date).toLocaleString('en-NG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
    catch { return 'N/A'; }
};

const formatTimeAgo = (date: string) => {
    try {
        const now = new Date();
        const then = new Date(date);
        const diffMs = now.getTime() - then.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatDateTime(date);
    } catch { return 'N/A'; }
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ErrorLogsPage() {
    const navigate = useNavigate();
    const user = useAuthStore(s => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    const [page, setPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [filters, setFilters] = useState({
        search: '',
        severity: '',
        errorType: '',
        startDate: '',
        endDate: '',
    });
    const [showFilters, setShowFilters] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
    const limit = 20;

    const queryParams = useMemo(() => {
        const params: Record<string, any> = { limit, page };
        if (filters.search) params.search = filters.search;
        if (filters.severity) params.severity = filters.severity;
        if (filters.errorType) params.errorType = filters.errorType;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
        return params;
    }, [filters, page]);

    const { data, isLoading, isError, refetch } = useQuery<ErrorLogsResponse>({
        queryKey: ['super-admin-error-logs', queryParams],
        queryFn: async () => {
            const { data } = await apiClient.get('/error-logs', { params: queryParams });
            return data?.data;
        },
        enabled: !!user,
    });

    const deleteMutation = useMutation({
        mutationFn: (ids: string[]) => apiClient.delete('/error-logs', { data: { ids } }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-error-logs'] });
            addToast({ type: 'success', title: 'Error Logs Deleted', message: 'Selected error logs have been permanently deleted.' });
            setSelectedIds(new Set());
            setConfirmDeleteId(null);
            setConfirmBulkDelete(false);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Delete Failed', message: err?.message }),
    });

    const logs = data?.logs || [];
    const summary = data?.summary;
    const totalCount = data?.totalCount || 0;
    const totalPages = Math.ceil(totalCount / limit);
    const hasActiveFilters = !!(filters.search || filters.severity || filters.errorType || filters.startDate || filters.endDate);

    const handleFilterChange = (key: keyof typeof filters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({ search: '', severity: '', errorType: '', startDate: '', endDate: '' });
        setPage(1);
    };

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === logs.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(logs.map(l => l.id)));
        }
    };

    const handleSingleDelete = (id: string) => {
        deleteMutation.mutate([id]);
    };

    const handleBulkDelete = () => {
        deleteMutation.mutate(Array.from(selectedIds));
    };

    if (isLoading) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 rounded-2xl border-4 border-rose-200 border-t-rose-600" />
        </div>
    );

    if (isError) return (
        <div className="min-h-screen flex items-center justify-center">
            <ErrorState title="Failed to load error logs" onRetry={() => refetch()} />
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/30">
            <div className="max-w-[1800px] mx-auto p-4 lg:p-6 space-y-5">

                {/* Header */}
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <button onClick={() => navigate('/super-admin')}
                        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-rose-600 mb-3 group">
                        <ArrowLeft size={16} className="group-hover:-translate-x-1" /> Back to Dashboard
                    </button>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center shadow-lg">
                                <Server size={20} className="text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">System Error Logs</h1>
                                <p className="text-slate-500 text-sm mt-0.5">{totalCount.toLocaleString()} errors captured across the platform</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => refetch()}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm">
                                <RefreshCw size={14} /> Refresh
                            </button>
                            {selectedIds.size > 0 && (
                                <button onClick={() => setConfirmBulkDelete(true)}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-xl font-semibold shadow-lg text-sm hover:from-rose-600 hover:to-red-700">
                                    <Trash2 size={16} /> Delete {selectedIds.size} Selected
                                </button>
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* Summary Metrics */}
                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {[
                            { label: 'Total Errors', value: summary.totalErrors.toLocaleString(), icon: Server, gradient: 'from-slate-500 to-slate-700' },
                            { label: 'Critical', value: summary.criticalErrors.toLocaleString(), icon: XCircle, gradient: 'from-rose-500 to-red-600' },
                            { label: 'Server Errors', value: summary.serverErrors.toLocaleString(), icon: Zap, gradient: 'from-red-500 to-pink-600' },
                            { label: 'Validation', value: summary.validationErrors.toLocaleString(), icon: Shield, gradient: 'from-amber-500 to-orange-600' },
                            { label: 'Today', value: summary.todayErrors.toLocaleString(), icon: Calendar, gradient: 'from-blue-500 to-indigo-600' },
                        ].map((stat, i) => (
                            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-all">
                                <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.gradient} shadow-md w-fit mb-3`}>
                                    <stat.icon className="w-4 h-4 text-white" />
                                </div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{stat.label}</p>
                                <p className="text-xl font-bold text-slate-900 mt-0.5">{stat.value}</p>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Search & Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 space-y-3">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input type="text" placeholder="Search by error message, path, or code..." value={filters.search}
                                onChange={e => handleFilterChange('search', e.target.value)}
                                className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-rose-500/20" />
                            {filters.search && (
                                <button onClick={() => handleFilterChange('search', '')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400"><X size={14} /></button>
                            )}
                        </div>
                        <button onClick={() => setShowFilters(!showFilters)}
                            className={clsx('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all',
                                showFilters || hasActiveFilters ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-slate-50 text-slate-600 border-slate-200')}>
                            <Filter size={14} /> Filters
                            {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-rose-500" />}
                        </button>
                    </div>

                    <AnimatePresence>
                        {showFilters && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                                className="overflow-hidden border-t border-slate-100 pt-3">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Severity</label>
                                        <select value={filters.severity} onChange={e => handleFilterChange('severity', e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white">
                                            <option value="">All Severities</option>
                                            <option value="critical">Critical</option>
                                            <option value="error">Error</option>
                                            <option value="warning">Warning</option>
                                            <option value="info">Info</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Error Type</label>
                                        <select value={filters.errorType} onChange={e => handleFilterChange('errorType', e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white">
                                            <option value="">All Types</option>
                                            <option value="validation">Validation</option>
                                            <option value="unauthorized">Unauthorized</option>
                                            <option value="forbidden">Forbidden</option>
                                            <option value="not_found">Not Found</option>
                                            <option value="conflict">Conflict</option>
                                            <option value="server_error">Server Error</option>
                                            <option value="database_error">Database Error</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Start Date</label>
                                        <input type="date" value={filters.startDate} onChange={e => handleFilterChange('startDate', e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">End Date</label>
                                        <input type="date" value={filters.endDate} onChange={e => handleFilterChange('endDate', e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs" />
                                    </div>
                                </div>
                                {hasActiveFilters && (
                                    <div className="flex justify-end mt-3 pt-2 border-t border-slate-100">
                                        <button onClick={clearFilters} className="text-xs text-rose-600 font-medium hover:text-rose-700">Clear All Filters</button>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Error Logs Table */}
                {logs.length === 0 ? (
                    <EmptyState icon={<Server size={48} />} title="No error logs found"
                        description={hasActiveFilters ? 'Try adjusting your filters.' : 'No errors have been captured yet. Great job!'} />
                ) : (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-3 py-3 text-center w-10">
                                            <button onClick={toggleSelectAll} className="p-1 hover:bg-slate-200 rounded" title="Select all">
                                                {selectedIds.size === logs.length ? <CheckSquare size={16} className="text-rose-600" /> : <Square size={16} className="text-slate-400" />}
                                            </button>
                                        </th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase w-36">Timestamp</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase">Severity</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase">Type</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase">Error Message</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase hidden lg:table-cell">Path</th>
                                        <th className="px-4 py-3 text-center text-[10px] font-bold text-slate-500 uppercase w-16">Status</th>
                                        <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase w-24">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {logs.map((log, idx) => {
                                        const severityConfig = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.info;
                                        const SeverityIcon = severityConfig.icon;
                                        const typeConfig = ERROR_TYPE_CONFIG[log.errorType] || ERROR_TYPE_CONFIG.unknown;
                                        const TypeIcon = typeConfig.icon;
                                        const isSelected = selectedIds.has(log.id);

                                        return (
                                            <motion.tr key={log.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.01 }}
                                                className={clsx(
                                                    'hover:bg-rose-50/20 transition-colors group cursor-pointer',
                                                    isSelected && 'bg-rose-50/50'
                                                )}
                                                onClick={() => navigate(`/super-admin/error-logs/${log.id}`)}>
                                                <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                                                    <button onClick={() => toggleSelect(log.id)} className="p-1 hover:bg-slate-200 rounded">
                                                        {isSelected ? <CheckSquare size={16} className="text-rose-600" /> : <Square size={16} className="text-slate-400" />}
                                                    </button>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-col">
                                                        <span className="text-xs font-mono text-slate-600">{formatDateTime(log.createdAt)}</span>
                                                        <span className="text-[10px] text-slate-400">{formatTimeAgo(log.createdAt)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={clsx('inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold', severityConfig.bg, severityConfig.color)}>
                                                        <SeverityIcon size={10} /> {severityConfig.label}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={clsx('inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold', typeConfig.bg, typeConfig.color)}>
                                                        <TypeIcon size={10} /> {typeConfig.label}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <p className="text-xs text-slate-700 max-w-[300px] truncate" title={log.message}>
                                                        {log.message}
                                                    </p>
                                                    {log.errorCode && (
                                                        <code className="text-[10px] text-slate-400 font-mono">{log.errorCode}</code>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 hidden lg:table-cell">
                                                    <code className="text-[10px] text-slate-400 font-mono truncate max-w-[200px] block">
                                                        {log.requestMethod} {log.requestPath}
                                                    </code>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={clsx(
                                                        'inline-block px-2 py-1 rounded-lg text-[10px] font-bold',
                                                        log.responseStatus && log.responseStatus >= 500 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                                    )}>
                                                        {log.responseStatus || '—'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                                                    <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button onClick={() => navigate(`/super-admin/error-logs/${log.id}`)}
                                                            className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50" title="View Details">
                                                            <Eye size={14} />
                                                        </button>
                                                        <button onClick={() => setConfirmDeleteId(log.id)}
                                                            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50" title="Delete">
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                                <span className="text-xs text-slate-500">Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, totalCount)} of {totalCount.toLocaleString()}</span>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => setPage(1)} disabled={page === 1} className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30"><ChevronsLeft size={14} /></button>
                                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30"><ChevronLeft size={14} /></button>
                                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                        let pageNum: number;
                                        if (totalPages <= 7) pageNum = i + 1;
                                        else if (page <= 4) pageNum = i + 1;
                                        else if (page >= totalPages - 3) pageNum = totalPages - 6 + i;
                                        else pageNum = page - 3 + i;
                                        return (
                                            <button key={pageNum} onClick={() => setPage(pageNum)}
                                                className={clsx('w-8 h-8 rounded-lg text-xs font-medium', page === pageNum ? 'bg-rose-600 text-white' : 'text-slate-600 hover:bg-slate-200')}>{pageNum}</button>
                                        );
                                    })}
                                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30"><ChevronRight size={14} /></button>
                                    <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30"><ChevronsRight size={14} /></button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Single Delete Confirmation */}
                <AnimatePresence>
                    {confirmDeleteId && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                            onClick={() => setConfirmDeleteId(null)}>
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
                                <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
                                    <Trash2 size={28} className="text-rose-500" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900">Delete Error Log?</h3>
                                <p className="text-sm text-slate-500 mt-2">This will permanently remove this error log. This action cannot be undone.</p>
                                <div className="flex gap-3 mt-6">
                                    <button onClick={() => setConfirmDeleteId(null)}
                                        className="flex-1 px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50">Cancel</button>
                                    <button onClick={() => { handleSingleDelete(confirmDeleteId); }}
                                        disabled={deleteMutation.isPending}
                                        className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold hover:bg-rose-600 disabled:opacity-50 flex items-center justify-center gap-2">
                                        {deleteMutation.isPending ? <Spinner size="xs" variant="white" /> : <Trash2 size={14} />} Delete
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Bulk Delete Confirmation */}
                <AnimatePresence>
                    {confirmBulkDelete && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                            onClick={() => setConfirmBulkDelete(false)}>
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
                                <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
                                    <Trash2 size={28} className="text-rose-500" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900">Delete {selectedIds.size} Error Logs?</h3>
                                <p className="text-sm text-slate-500 mt-2">This will permanently remove {selectedIds.size} selected error logs. This action cannot be undone.</p>
                                <div className="flex gap-3 mt-6">
                                    <button onClick={() => setConfirmBulkDelete(false)}
                                        className="flex-1 px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50">Cancel</button>
                                    <button onClick={handleBulkDelete}
                                        disabled={deleteMutation.isPending}
                                        className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold hover:bg-rose-600 disabled:opacity-50 flex items-center justify-center gap-2">
                                        {deleteMutation.isPending ? <Spinner size="xs" variant="white" /> : <Trash2 size={14} />} Delete All
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}